import { WelcomeTutorial } from './WelcomeTutorial'
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { Icon, type IconName } from './Icon'
import { api } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { PENDING_PROFILE_KEY, ProfileProvider, useProfile } from '@/auth/ProfileContext'
import { Avatar } from '@/components/Avatar'
import { DeveloperProfileDialog } from './DeveloperProfileDialog'
import { ThemeSelector } from './ThemeProvider'
import { useSidebarCollapsed } from './useSidebarCollapsed'
import { SidebarResizeHandle } from './SidebarResizeHandle'
import { profileComplete, type Role, type User } from '@/lib/types'
import { useAsync } from './useAsync'
import { isScienceExpert } from '@/lib/programs'

const NAV: Record<Role, { to: string; label: string; icon: IconName }[]> = {
  sme: [
    { to: '/sme', label: 'My tasks', icon: 'tasks' },
    { to: '/sme/task-directory', label: 'All tasks', icon: 'grid' },
    { to: '/sme/submit', label: 'New task', icon: 'plus' },
    { to: '/sme/reviews', label: 'Reviews', icon: 'check' },
    { to: '/sme/guide', label: 'Guide', icon: 'book' },
  ],
  engineer: [
    { to: '/eng', label: 'Pipeline', icon: 'grid' },
    { to: '/eng/tasks', label: 'Tasks and workflows', icon: 'tasks' },
    { to: '/eng/runs', label: 'Run', icon: 'zap' },
    { to: '/eng/agents', label: 'Agents', icon: 'agent' },
    { to: '/eng/remi', label: 'REMI', icon: 'activity' },
    { to: '/eng/smes', label: 'Experts', icon: 'people' },
    { to: '/eng/docs', label: 'Docs', icon: 'file' },
  ],
}

export function Layout({ role }: { role: Role }) {
  return (
    <ProfileProvider>
      <Shell role={role} />
    </ProfileProvider>
  )
}

function Shell({ role }: { role: Role }) {
  const { user, signOut } = useAuth()
  const { profile, error, unauthorized, loading, setProfile, reload } = useProfile()
  const navigate = useNavigate()
  const location = useLocation()
  const intakeMeta = useAsync(() => role === 'sme' ? api.get<{ science_template_intake_enabled?: boolean }>('/meta') : Promise.resolve(null), [role])
  const [menuOpen, setMenuOpen] = useState(false)
  const [changingAvatar, setChangingAvatar] = useState(false)
  const [collapsed, setCollapsed] = useSidebarCollapsed()
  const [sidebarResize, setSidebarResize] = useState<boolean | null>(null)
  const compact = sidebarResize ?? collapsed
  useEffect(() => {
    const mobile = window.matchMedia('(max-width: 760px)')
    const closeDrawer = () => {
      if (!mobile.matches) setMenuOpen(false)
    }
    mobile.addEventListener('change', closeDrawer)
    return () => mobile.removeEventListener('change', closeDrawer)
  }, [])
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])
  useEffect(() => {
    if (!menuOpen) return
    const previous = document.activeElement as HTMLElement | null
    const sidebar = document.getElementById('app-sidebar')!
    const focusable = () =>
      Array.from(sidebar.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')).filter(
        (el) => el.offsetParent !== null,
      )
    focusable()[0]?.focus()
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
      if (e.key === 'Tab') {
        const items = focusable(),
          first = items[0],
          last = items[items.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last?.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first?.focus()
        }
      }
    }
    window.addEventListener('keydown', close)
    return () => {
      window.removeEventListener('keydown', close)
      previous?.focus()
    }
  }, [menuOpen])

  const [pendingSave, setPendingSave] = useState(() => !!sessionStorage.getItem(PENDING_PROFILE_KEY))
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'failed'>('idle')
  const [saveError, setSaveError] = useState('')
  const [saveAttempt, setSaveAttempt] = useState(0)
  const saving = useRef(false)
  const alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])

  const profileLoaded = !!profile

  // Profile details entered at sign-up are saved on the first signed-in page load.
  useEffect(() => {
    const raw = sessionStorage.getItem(PENDING_PROFILE_KEY)
    if (!raw || !profileLoaded) return
    if (saving.current) return
    const body: Record<string, unknown> = {}
    try {
      const pending = JSON.parse(raw) as Partial<User>
      if (pending.bio) body.bio = pending.bio
      if (pending.avatar) body.avatar = { kind: pending.avatar.kind, value: pending.avatar.value }
      if (pending.name) body.name = pending.name
      if (pending.programs?.length) body.programs = pending.programs
      if (pending.domain) body.domain = pending.domain
      if (pending.areas?.length) body.areas = pending.areas
    } catch {
      sessionStorage.removeItem(PENDING_PROFILE_KEY)
    }
    if (!Object.keys(body).length) {
      sessionStorage.removeItem(PENDING_PROFILE_KEY)
      setPendingSave(false)
      return
    }
    saving.current = true
    setSaveState('saving')
    api.put<User>('/me', body).then(me => {
      if (!alive.current) return
      if (sessionStorage.getItem(PENDING_PROFILE_KEY) === raw) sessionStorage.removeItem(PENDING_PROFILE_KEY)
      setPendingSave(!!sessionStorage.getItem(PENDING_PROFILE_KEY))
      setSaveState('idle')
      setSaveError('')
      setProfile(me)
    }).catch(e => {
      if (!alive.current) return
      setSaveState('failed')
      setSaveError(e instanceof Error ? e.message : 'Could not save your signup details.')
    }).finally(() => { saving.current = false })
  // Profile refreshes must not repeatedly send a failed signup save. Retry is explicit.
  }, [profileLoaded, saveAttempt, setProfile])

  if (loading || !profile) return <main className="page" aria-live="polite">
    {loading ? <p>Loading your profile…</p> : <section className="box">
      <h1>Could not load your profile</h1>
      {unauthorized ? <>
        <p className="err">Your sign-in is no longer valid. Sign in again to continue.</p>
        <button onClick={async () => { await signOut(); navigate('/') }}>Sign in again</button>
      </> : <>
        <p className="err">{error}</p>
        <div className="row">
          <button onClick={() => void reload()}>Retry profile loading</button>
          <button className="ghost" onClick={async () => { await signOut(); navigate('/') }}>Sign out</button>
        </div>
      </>}
    </section>}
  </main>
  if (saveState !== 'idle' || pendingSave) return <main className="page" aria-live="polite">
    {saveState !== 'failed' ? <p>Saving your signup details…</p> : <section className="box">
      <h1>Could not save your signup details</h1><p className="err">{saveError}</p>
      <p>Your details are still saved in this browser. Try again to continue.</p>
      <button onClick={() => setSaveAttempt(n => n + 1)}>Retry signup save</button>
    </section>}
  </main>

  // Experts must complete their profile (picture + bio) before doing anything else.
  const needsProfile =
    role === 'sme' &&
    !loading &&
    profile !== null &&
    !profileComplete(profile) &&
    !sessionStorage.getItem(PENDING_PROFILE_KEY)
  if (needsProfile && location.pathname !== '/sme/profile')
    return <Navigate to="/sme/profile" state={{ setup: true }} replace />

  const displayName = profile?.name || user?.name || user?.email
  // Developers must pick a picture and link GitHub before using the workspace; the card cannot be dismissed.
  const avatarDialog = role === 'engineer' && (changingAvatar || !profile.avatar?.value || !profile.github)
  const scienceCreation = role === 'sme' && isScienceExpert(profile) && intakeMeta.data?.science_template_intake_enabled
  // Science experts start from a template, which "New template" on My data creates; the sidebar has no create entry.
  const navigation = NAV[role].filter(n => !(role === 'sme' && n.to === '/sme/submit' && isScienceExpert(profile))).map(n =>
    role === 'sme' && isScienceExpert(profile) && n.to === '/sme' ? { ...n, label: scienceCreation ? 'My data' : 'My workflows' }
    : role === 'sme' && isScienceExpert(profile) && n.to === '/sme/task-directory' ? { ...n, label: scienceCreation ? 'All data' : 'All workflows' } : n)
  return (
    <div
      className={`app-shell${compact ? ' sidebar-collapsed' : ''}${sidebarResize !== null ? ' sidebar-resizing' : ''}`}
    >
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header className="mobile-header">
        <span className="brand">Seldon Expert Platform</span>
        <button
          className="ghost"
          aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={menuOpen}
          aria-controls="app-sidebar"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <Icon name={menuOpen ? 'close' : 'menu'} />
        </button>
      </header>
      {menuOpen && (
        <button
          className="sidebar-overlay"
          aria-label="Close navigation"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <aside id="app-sidebar" className={`app-sidebar ${menuOpen ? 'is-open' : ''}`}>
        <button
          className="drawer-close ghost"
          aria-label="Close navigation drawer"
          onClick={() => setMenuOpen(false)}
        >
          <Icon name="close" />
        </button>
        <div className="sidebar-heading">
          <NavLink to={role === 'sme' ? '/sme' : '/eng'} className="brand">
            <span>
              Seldon Expert Platform
              <small>{role === 'sme' ? 'Expert workspace' : 'Developer workspace'}</small>
            </span>
          </NavLink>
        </div>
        <nav className="sidebar-nav" aria-label="Main navigation">
          {navigation.map((n) => <NavLink
              key={n.to}
              to={n.to}
              aria-label={n.label}
              title={collapsed ? n.label : undefined}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                isActive ||
                (role === 'sme' && n.to === '/sme' && location.pathname.startsWith('/sme/tasks/'))
                  ? 'active'
                  : undefined
              }
              end={n.to === '/sme' || n.to === '/eng'}
            >
              <Icon name={n.icon} />
              <span className="sidebar-label">{n.label}</span>
            </NavLink>
          )}
        </nav>
        <div className="sidebar-bottom">
          <ThemeSelector sidebar />
          {role === 'sme' ? (
            <NavLink
              to="/sme/profile"
              className="account"
              aria-label={`${displayName}, your profile`}
              title={collapsed ? `${displayName} · Your profile` : undefined}
            >
              <Avatar avatar={profile?.avatar} name={displayName} size={34} />
              <span className="sidebar-account-label">
                <strong>{displayName}</strong>
                <small>Your profile</small>
              </span>
            </NavLink>
          ) : (
            <button
              type="button"
              className="account account-button"
              aria-label={`${displayName}, edit your developer profile`}
              title={collapsed ? `${displayName} · Developer profile` : 'Edit your picture and GitHub account'}
              onClick={() => setChangingAvatar(true)}
            >
              <Avatar avatar={profile.avatar} name={displayName} size={34} />
              <span className="sidebar-account-label">
                <strong>{displayName}</strong>
                <small>{profile.github ? `Developer · @${profile.github.login}` : 'Developer'}</small>
              </span>
            </button>
          )}
          <button
            className="ghost signout"
            aria-label="Sign out"
            title={collapsed ? 'Sign out' : undefined}
            onClick={async () => {
              await signOut()
              navigate('/')
            }}
          >
            <Icon name="logout" size={16} />
            <span className="sidebar-label">Sign out</span>
          </button>
        </div>
      </aside>
      <SidebarResizeHandle collapsed={collapsed} onChange={setCollapsed} onResize={setSidebarResize} />
      <main className="page app-page" id="main-content">
        {location.state?.reviewerNotice && (
          <p className="note" role="status">{location.state.reviewerNotice}</p>
        )}
        <Outlet />
        {role === 'sme' && <WelcomeTutorial />}
        {avatarDialog && <DeveloperProfileDialog profile={profile}
          onSaved={(u) => { setProfile(u); setChangingAvatar(false) }} onClose={() => setChangingAvatar(false)} />}
      </main>
    </div>
  )
}
